import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Select, Button, Space, Typography, message, Table, DatePicker, Tabs } from 'antd';
import { FileTextOutlined, DownloadOutlined, FunnelPlotOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { fetchVacancies } from '../../redux/vacanciesSlice';
import { fetchUsersByOrganisations } from '../../redux/userOrganisationsSlice';
import { fetchVacancyReport, exportVacancyReportToExcel } from '../../redux/analyticsSlice';
import moment from 'moment';
import 'moment/locale/ru';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Статусы вакансий (используем реальные статусы из системы)
const ACTIVE_STATUSES = ['new', 'in_progress', 'stopped', 'hanging', 'closed'];

const FAILURE_REASONS = {
  no_show: 'Не пришел на собеседование',
  irrelevant_experience: 'Нерелевантный опыт работы',
  irrelevant_salary: 'Нерелевантный доход',
  no_contact: 'Не выходит на связь',
  not_interested_product: 'Не интересен товар',
  unsuitable_schedule: 'Не подходит график работы',
  not_actively_searching: 'Не актуален поиск работы',
  not_interested_functionality: 'Не интересен функционал',
  long_commute: 'Далеко добираться',
  failed_test_assignment: 'Не прошел тестовое задание',
  vacancy_closed: 'Вакансия закрыта',
  other_requirements: 'Не подходит по другим требованиям'
};

const STATUS_LABELS = {
  new: 'Новая',
  in_progress: 'В работе',
  stopped: 'Приостановлена',
  hanging: 'Зависшая',
  closed: 'Закрыта',
  template: 'Черновик'
};

const VacancyReportsPage = () => {
  const dispatch = useDispatch();
  const { orgId, role, userOrganisations } = useSelector((state) => state.auth);
  const { organisations } = useSelector((state) => state.organisationDepartment);
  const { list: vacancies, loading: vacanciesLoading, showAllVacancies } = useSelector((state) => state.vacancies);
  const { organisationUsers: users, isLoading: usersLoading } = useSelector((state) => state.userOrganisations);
  const { isLoading: reportLoading, isExporting } = useSelector((state) => state.analytics);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [reportData, setReportData] = useState(null);

  // Ref для debounce таймера
  const debounceTimerRef = useRef(null);

  const availableOrganisations = useMemo(() => {
    if (role === 'superadmin') {
      return organisations;
    }
    return organisations.filter(org =>
      userOrganisations.some(userOrg =>
        (userOrg.organisation_id === org.id || userOrg.organisation_id === org.organisation_id)
      )
    );
  }, [role, organisations, userOrganisations]);

  useEffect(() => {
    if (orgId && !selectedOrganisation) {
      setSelectedOrganisation(orgId);
    } else if (role === 'superadmin' && availableOrganisations.length > 0 && !selectedOrganisation) {
      setSelectedOrganisation(availableOrganisations[0].id || availableOrganisations[0].organisation_id);
    }
  }, [orgId, role, availableOrganisations, selectedOrganisation]);

  useEffect(() => {
    const orgIdToFetch = (role === 'superadmin' || showAllVacancies) ? "all" : orgId;
    dispatch(fetchVacancies({ skip: 0, limit: 1000, org_id: orgIdToFetch }));
  }, [dispatch, orgId, role, showAllVacancies]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!selectedOrganisation) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      dispatch(fetchUsersByOrganisations([selectedOrganisation]));
    }, 1500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [dispatch, selectedOrganisation]);

  const recruiters = useMemo(() => {
    if (!users || users.length === 0) return [];

    return users.filter(user =>
      user.role === 'hr' || user.role === 'admin'
    );
  }, [users]);

  const filteredVacancies = useMemo(() => {
    if (!vacancies || vacancies.length === 0) return [];

    let filtered = vacancies;

    filtered = filtered.filter(v => ACTIVE_STATUSES.includes(v.status) && !v.is_template);

    if (selectedOrganisation) {
      filtered = filtered.filter(v => v.organisation_id === selectedOrganisation);
    }

    if (selectedRecruiter) {
      filtered = filtered.filter(v => v.assigner_id === selectedRecruiter);
    }

    return filtered;
  }, [vacancies, selectedOrganisation, selectedRecruiter]);

  const handleGenerateReport = async () => {
    if (!selectedVacancy) {
      message.warning('Пожалуйста, выберите вакансию');
      return;
    }

    try {
      const asOfDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : null;

      const apiReport = await dispatch(fetchVacancyReport({
        vacancyId: selectedVacancy,
        asOfDate
      })).unwrap();

      // Находим локальные данные для дополнительной информации
      const vacancy = vacancies.find(v => v.id === selectedVacancy);
      const vacancyOrg = organisations.find(o =>
        (o.id === vacancy?.organisation_id || o.organisation_id === vacancy?.organisation_id)
      );
      const recruiter = recruiters.find(r => r.user_id === (selectedRecruiter || vacancy?.author));

      // Комбинируем данные из API и локальные данные
      const report = {
        ...apiReport,
        vacancy: vacancy || apiReport.vacancy,
        generatedAt: moment().format('DD.MM.YYYY HH:mm'),
        organisation: vacancyOrg?.name || apiReport.organisation_name || 'Не указана',
        recruiter: recruiter || apiReport.recruiter,
        dateRange: dateRange ? {
          start: dateRange[0].format('DD.MM.YYYY'),
          end: dateRange[1].format('DD.MM.YYYY')
        } : null
      };

      setReportData(report);
      message.success('Отчет успешно сгенерирован');
    } catch (error) {
      message.error('Ошибка при генерации отчета');
      console.error(error);
    }
  };

  // Сброс фильтров
  const handleReset = () => {
    setSelectedOrganisation(orgId || null);
    setSelectedRecruiter(null);
    setSelectedVacancy(null);
    setDateRange(null);
    setReportData(null);
  };

  const handleExportToExcel = async () => {
    if (!selectedVacancy) {
      message.warning('Пожалуйста, выберите вакансию');
      return;
    }

    try {
      await dispatch(exportVacancyReportToExcel(selectedVacancy)).unwrap();
      message.success('Отчет успешно экспортирован');
    } catch (error) {
      message.error('Ошибка при экспорте отчета');
      console.error(error);
    }
  };

  const funnelColumns = [
    {
      title: 'Этап',
      dataIndex: 'stage',
      key: 'stage',
    },
    {
      title: 'Количество',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
    },
  ];

  const failuresColumns = [
    {
      title: 'Причина отказа',
      dataIndex: 'failure_reason',
      key: 'failure_reason',
    },
    {
      title: 'Количество',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: 'Доля, %',
      dataIndex: 'share',
      key: 'share',
    }
  ];

  const funnelAllTimeData = useMemo(() => {
    if (!reportData?.funnel_all_time) return [];

    return Object.entries(reportData.funnel_all_time).map(([stage, count], index) => ({
      key: `all-${index}`,
      stage,
      count,
    }));
  }, [reportData]);

  const funnelCurrentData = useMemo(() => {
    if (!reportData?.funnel_current) return [];

    return Object.entries(reportData.funnel_current).map(([stage, count], index) => ({
      key: `current-${index}`,
      stage,
      count: count || 0,
    }));
  }, [reportData]);

  const failuresData = useMemo(() => {
    if (!reportData?.failures || reportData.failures.length === 0) return [];

    const failureReasonsArray = Object.keys(FAILURE_REASONS).map(key => ({
      key: key,
      title: FAILURE_REASONS[key]
    }));

    return failureReasonsArray.map(reason => ({
      key: `failure-${reason.key}`,
      count: reportData.failures.find(failure => failure.reason === reason.key)?.count || 0,
      share: reportData.failures.find(failure => failure.reason === reason.key)?.share || 0,
      failure_reason: reason.title,
    }));

    // return reportData.failures.map((failure, index) => ({
    //   key: `failure-${index}`,
    //   count: failure.count,
    //   share: failure.share,
    //   failure_reason: FAILURE_REASONS[failure.reason] || failure.reason || 'Не указана',
    // }));
  }, [reportData]);

  const chartDataAllTime = useMemo(() => {
    if (!funnelAllTimeData || funnelAllTimeData.length === 0) return null;

    return {
      labels: funnelAllTimeData.map(item => item.stage),
      datasets: [
        {
          label: 'Количество кандидатов',
          data: funnelAllTimeData.map(item => item.count),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [funnelAllTimeData]);

  const chartDataCurrent = useMemo(() => {
    if (!funnelCurrentData || funnelCurrentData.length === 0) return null;

    return {
      labels: funnelCurrentData.map(item => item.stage),
      datasets: [
        {
          label: 'Количество кандидатов',
          data: funnelCurrentData.map(item => item.count),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [funnelCurrentData]);

  const chartDataFailures = useMemo(() => {
    if (!failuresData || failuresData.length === 0) return null;

    return {
      labels: failuresData.map(item => item.failure_reason),
      datasets: [
        {
          label: 'Количество',
          data: failuresData.map(item => item.count),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [failuresData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Настройки для горизонтального графика (отказы)
  const chartOptionsHorizontal = {
    indexAxis: 'y', // Это делает график горизонтальным
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <FileTextOutlined style={{ fontSize: '24px', marginRight: '12px' }} />
            <Title level={3} style={{ display: 'inline' }}>
              Отчетность по вакансиям
            </Title>
          </div>

          {/* Фильтры */}
          <Card title="Фильтры" size="small">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Организация */}
              {role === 'superadmin' && (
                <div>
                  <Text strong>Организация:</Text>
                  <Select
                    style={{ width: '100%', marginTop: '8px' }}
                    placeholder="Выберите организацию"
                    value={selectedOrganisation}
                    onChange={(value) => {
                      setSelectedOrganisation(value);
                      setSelectedRecruiter(null);
                      setSelectedVacancy(null);
                    }}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {availableOrganisations.map((org) => (
                      <Select.Option key={org.id || org.organisation_id} value={org.id || org.organisation_id}>
                        {org.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Рекрутер */}
              <div>
                <Text strong>Рекрутер:</Text>
                <Select
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="Выберите рекрутера (необязательно)"
                  value={selectedRecruiter}
                  onChange={(value) => {
                    setSelectedRecruiter(value);
                    setSelectedVacancy(null);
                  }}
                  allowClear
                  loading={usersLoading}
                  disabled={!selectedOrganisation}
                >
                  {recruiters.map((user) => (
                    <Select.Option key={user.id} value={user.user_id}>
                      {user.first_name} {user.last_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong>Вакансия: *</Text>
                <Select
                  style={{ width: '100%', marginTop: '8px' }}
                  placeholder="Выберите вакансию"
                  value={selectedVacancy}
                  onChange={setSelectedVacancy}
                  loading={vacanciesLoading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {filteredVacancies.map((vacancy) => {
                    const org = organisations.find(o =>
                      o.id === vacancy.organisation_id || o.organisation_id === vacancy.organisation_id
                    );
                    return (
                      <Select.Option key={vacancy.id} value={vacancy.id}>
                        {vacancy.job_title} (ID: {vacancy.id}) - {STATUS_LABELS[vacancy.status]} - {org?.name || ''}
                      </Select.Option>
                    );
                  })}
                </Select>
                {filteredVacancies.length === 0 && (
                  <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                    Нет активных вакансий для выбранных критериев
                  </Text>
                )}
              </div>

              <div>
                <Text strong>Период отчета (необязательно):</Text>
                <RangePicker
                  style={{ width: '100%', marginTop: '8px' }}
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD.MM.YYYY"
                  placeholder={['Дата начала', 'Дата окончания']}
                />
              </div>
            </Space>
          </Card>

          {/* Кнопки действий */}
          <Space>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={handleGenerateReport}
              loading={reportLoading}
              disabled={!selectedVacancy}
            >
              Сгенерировать отчет
            </Button>
            <Button onClick={handleReset}>
              Сбросить фильтры
            </Button>
          </Space>

          {/* Результат отчета */}
          {reportData && (
            <Card>
              {/* Заголовок с кнопкой экспорта */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    Отчет по вакансии: {reportData.vacancy?.job_title || 'Не указана'}
                  </Title>
                  <Text type="secondary">
                    Дата формирования: {reportData.generatedAt}
                  </Text>
                </div>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportToExcel}
                  loading={isExporting}
                >
                  Экспорт в Excel
                </Button>
              </div>

              {/* Табы с данными */}
              <Tabs
                defaultActiveKey="funnel_all"
                items={[
                  {
                    key: 'funnel_all',
                    label: (
                      <span>
                        <FunnelPlotOutlined /> Воронка за все время
                      </span>
                    ),
                    children: (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Table
                          columns={funnelColumns}
                          dataSource={funnelAllTimeData}
                          pagination={false}
                          size="small"
                          bordered
                        />
                        {chartDataAllTime && (
                          <div style={{ height: '400px', marginTop: '24px' }}>
                            <Bar data={chartDataAllTime} options={chartOptions} />
                          </div>
                        )}
                      </Space>
                    ),
                  },
                  {
                    key: 'funnel_current',
                    label: (
                      <span>
                        <FunnelPlotOutlined /> Текущая воронка
                      </span>
                    ),
                    children: (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Table
                          columns={funnelColumns}
                          dataSource={funnelCurrentData}
                          pagination={false}
                          size="small"
                          bordered
                        />
                        {chartDataCurrent && (
                          <div style={{ height: '400px', marginTop: '24px' }}>
                            <Bar data={chartDataCurrent} options={chartOptions} />
                          </div>
                        )}
                      </Space>
                    ),
                  },
                  {
                    key: 'failures',
                    label: (
                      <span>
                        <CloseCircleOutlined /> Отказы ({failuresData.length})
                      </span>
                    ),
                    children: (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Table
                          columns={failuresColumns}
                          dataSource={failuresData}
                          pagination={failuresData.length > 20 ? { pageSize: 20 } : false}
                          size="small"
                          bordered
                          locale={{ emptyText: 'Нет отказов' }}
                        />
                        {chartDataFailures && (
                          <div style={{ height: '600px', marginTop: '24px' }}>
                            <Bar data={chartDataFailures} options={chartOptionsHorizontal} />
                          </div>
                        )}
                      </Space>
                    ),
                  },
                ]}
              />
            </Card>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default VacancyReportsPage;

